import { Insurer } from '@/codebook/insurer/_types/insurer.type';
import { Insurance } from '@/codebook/insurer/insurer-package/_types/insurer-package.type';
import { ProductKind } from '@/common/product-kind.enum';
import { ProductApiService } from '@/products/product/product.api.service';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { ClassicEditor } from '@/common/editor/classic-editor';
import {
	EMPTY,
	Observable,
	Subject,
	combineLatest,
	debounceTime,
	distinctUntilChanged,
	map,
	switchMap,
	takeUntil,
} from 'rxjs';
import { DataBagService } from 'src/app/common/data-bag.service';
import { ProductService } from '../product.service';

@Component({
	selector: 'app-product-basic',
	templateUrl: './product-basic.component.html',
	styleUrls: ['./product-basic.component.css'],
	providers: [
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: {
				subscriptSizing: 'dynamic',
			},
		},
	],
})
export class ProductBasicComponent implements OnInit, OnDestroy {
	@Input() editable: boolean;
	@Input() pageFunction: string;
	editor = ClassicEditor;
	editorConfig = {
		toolbar: ['bold', 'italic', 'underline', '|', 'bulletedList', 'numberedList'],
	};
	statusSet: Array<{ value: string; name: string }> = [
		{ value: 'D', name: 'Szkic' },
		{ value: 'P', name: 'Opublikowany' },
		{ value: 'A', name: 'Zarchiwizowany' },
	];

	destroys$ = new Subject<void>();

	readonly ProductKind = ProductKind;

	readonly insurerFormControl = new FormControl<Insurer>(null, [Validators.required]);
	readonly insuranceFormControl = new FormControl<null>(null, [Validators.required]);

	readonly filteredInsurers$: Observable<Insurer[]> = this.insurerFormControl.valueChanges.pipe(
		distinctUntilChanged(),
		debounceTime(400),
		switchMap((searchValue: Insurer | string) => {
			if (searchValue === null || searchValue === '' || typeof searchValue === 'object') {
				return this.apiService.getInsurers('&active=true');
			}

			return this.apiService.getInsurersByName(searchValue as string, '&active=true');
		}),
		map((response) => response.results),
	);

	readonly filteredInsurances$: Observable<Insurance[]> = combineLatest([
		this.insuranceFormControl.valueChanges,
		this.insurerFormControl.valueChanges,
	]).pipe(
		distinctUntilChanged(),
		debounceTime(400),
		switchMap(([searchValue]) => {
			if (!this.insurerFormControl.value) {
				return EMPTY;
			}

			if (searchValue === null || searchValue === '' || typeof searchValue === 'object') {
				return this.apiService.getInsurances(this.insurerFormControl.value.id, '&active=true&is_default=false');
			}

			return this.apiService.getInsurancesByName(
				this.insurerFormControl.value.id,
				searchValue as string,
				'&active=true&is_default=false',
			);
		}),
		map((response) => response.results),
	);

	constructor(
		public service: ProductService,
		private readonly bag: DataBagService,
		private readonly apiService: ProductApiService,
	) {}

	ngOnInit(): void {
		this.bag.get('/tag/?limit=999', (result, error) => {
			this.service.mockData.tags = result.results;
		});
		this.bag.get('/product/kind/', (result, error) => {
			this.service.mockData.productKind = result.results;
		});
		this.bag.get('/organization/?limit=999&is_oskar=true', (result, error) => {
			this.service.mockData.organization = result.results;
		});
		this.bag.get('/location/?limit=9999&stop_type=TG', (result, error) => {
			this.service.mockData.location = result.results;
		});

		this.service.dataLoaded$.pipe(takeUntil(this.destroys$)).subscribe(() => {
			this.insurerFormControl.patchValue(this.service.data.insurer);
			this.insuranceFormControl.patchValue(this.service.data.insurance);
		});
	}

	ngOnDestroy(): void {
		this.destroys$.next();
		this.destroys$.complete();
	}

	changeProductType(): void {
		this.service.mockData.code1 = this.service.mockData.productKind.find(
			(findObj) => findObj.id == this.service.data.productKind,
		).code;
		if ([2, 3].includes(this.service.data.productKind)) {
			this.service.mockData.code2 = 'EU';
		}
		if ([1, 5, 6].includes(this.service.data.productKind)) {
			this.service.mockData.stops = this.service.mockData.stops.filter(
				(item) => item.parentStop !== null && item.stopType === 'DO',
			);
		}
		this.service.onChangeProductType.next(true);
		this.service.data.location = [];
	}

	changeLocationCode(): void {
		this.service.mockData.code2 = this.service.mockData.location.find(
			(findObj) => findObj.id == this.service.data.location,
		).code;
	}

	displayByName<T extends { name?: string }>(obj: T): string {
		return obj?.name || '';
	}

	selectInsurer($event: MatAutocompleteSelectedEvent): void {
		this.service.data.insurer = $event.option.value;
		this.insuranceFormControl.patchValue(null);
		this.service.data.insurance = null;
		this.insuranceFormControl.markAsTouched();
	}

	selectInsurance($event: MatAutocompleteSelectedEvent): void {
		this.service.data.insurance = $event.option.value;
	}
}
